import React, { useState, useEffect } from 'react';
import ReactSelect from 'react-select';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Textarea,
    VStack,
    HStack,
    Radio,
    RadioGroup,
    Select,
    useToast,
    Text,
    Box
} from '@chakra-ui/react';
import { FiSend } from 'react-icons/fi';
import attendanceService from '../services/attendanceService';

const SendSMSModal = ({ isOpen, onClose, users, attendanceGroups }) => {
    const [targetType, setTargetType] = useState('users');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedMatier, setSelectedMatier] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    const userOptions = users.map(user => ({
        value: user._id,
        label: `${user.username} (${user.parentPhoneNumber || user.phoneNumber || 'بلا هاتف'})`
    }));

    const availableGroups = attendanceGroups.find(g => g.matier === selectedMatier)?.groups || [];

    const handleSend = async () => {
        if (!message) {
            toast({ title: 'يرجى كتابة نص الرسالة', status: 'warning' });
            return;
        }

        if (targetType === 'users' && selectedUsers.length === 0) {
            toast({ title: 'يرجى اختيار مستخدم واحد على الأقل', status: 'warning' });
            return;
        }

        if (targetType === 'group' && (!selectedMatier || !selectedGroup)) {
            toast({ title: 'يرجى اختيار المادة والمجموعة', status: 'warning' });
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                targetType,
                message,
                userIds: targetType === 'users' ? selectedUsers.map(u => u.value) : [],
                matier: selectedMatier,
                groupName: selectedGroup
            };

            const response = await attendanceService.sendManualSMS(payload);
            if (response.success) {
                toast({
                    title: 'تمت العملية بنجاح',
                    description: response.message,
                    status: 'success'
                });
                onClose();
                setMessage('');
                setSelectedUsers([]);
                setSelectedMatier('');
                setSelectedGroup('');
            } else {
                toast({
                    title: 'خطأ',
                    description: response.message,
                    status: 'error'
                });
            }
        } catch (error) {
            toast({
                title: 'خطأ في الاتصال',
                description: error.response?.data?.message || 'فشل في إرسال الطلب',
                status: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" dir="rtl">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent borderRadius="xl" fontFamily="Cairo, sans-serif">
                <ModalHeader borderBottomWidth="1px">إرسال رسالة SMS مخصصة</ModalHeader>
                <ModalCloseButton left="8px" right="auto" />
                <ModalBody p={6}>
                    <VStack spacing={6} align="stretch">
                        <FormControl>
                            <FormLabel fontWeight="bold">إرسال إلى:</FormLabel>
                            <RadioGroup onChange={setTargetType} value={targetType}>
                                <HStack spacing={10}>
                                    <Radio value="users">مستخدمين محددين</Radio>
                                    <Radio value="group">مجموعة (WhatsApp)</Radio>
                                </HStack>
                            </RadioGroup>
                        </FormControl>

                        {targetType === 'users' ? (
                            <FormControl>
                                <FormLabel fontWeight="bold">اختر المستخدمين</FormLabel>
                                <ReactSelect
                                    isMulti
                                    options={userOptions}
                                    value={selectedUsers}
                                    onChange={setSelectedUsers}
                                    placeholder="ابحث عن المستخدمين..."
                                    styles={{
                                        control: (base) => ({ ...base, textAlign: 'right' }),
                                        menu: (base) => ({ ...base, textAlign: 'right' }),
                                    }}
                                />
                            </FormControl>
                        ) : (
                            <HStack align="end" spacing={4}>
                                <FormControl>
                                    <FormLabel fontWeight="bold">المادة</FormLabel>
                                    <Select
                                        placeholder="اختر المادة"
                                        value={selectedMatier}
                                        onChange={(e) => { setSelectedMatier(e.target.value); setSelectedGroup(''); }}
                                    >
                                        {attendanceGroups.map(g => (
                                            <option key={g._id} value={g.matier}>{g.matier}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontWeight="bold">المجموعة</FormLabel>
                                    <Select
                                        placeholder="اختر المجموعة"
                                        value={selectedGroup}
                                        onChange={(e) => setSelectedGroup(e.target.value)}
                                        isDisabled={!selectedMatier}
                                    >
                                        {availableGroups.map((g, i) => (
                                            <option key={i} value={g}>{g}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </HStack>
                        )}

                        <FormControl>
                            <FormLabel fontWeight="bold">نص الرسالة</FormLabel>
                            <Textarea
                                placeholder="اكتب رسالتك هنا..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={5}
                                borderRadius="lg"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                سيتم إرسال هذه الرسالة عبر تطبيق الأندرويد المرتبط بالنظام.
                            </Text>
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter bg="gray.50" borderBottomRadius="xl">
                    <HStack spacing={3}>
                        <Button
                            leftIcon={<FiSend />}
                            colorScheme="blue"
                            onClick={handleSend}
                            isLoading={isLoading}
                            loadingText="جاري الإرسال..."
                        >
                            إرسال الرسائل
                        </Button>
                        <Button variant="ghost" onClick={onClose}>إلغاء</Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SendSMSModal;
